import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import settingsService from '../../../../services/settings.service';

const SocialLinks = () => {
    const [socialLinks, setSocialLinks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [addingNew, setAddingNew] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm();

    // Social link types with their thumbnails
    const socialLinkTypes = [
        { value: 'WEBSITE', label: 'Website' },
        { value: 'GOOGLE_PAGE', label: 'Google Business'},
        { value: 'FACEBOOK', label: 'Facebook'},
        { value: 'INSTAGRAM', label: 'Instagram' },
        { value: 'TWITTER', label: 'Twitter' },
        { value: 'YELP', label: 'Yelp'},
        { value: 'OTHER', label: 'Other' },
    ];

    const fetchSocialLinks = async () => {
        setLoading(true);
        try {
            const response = await settingsService.getSocialLinks(clinicId);
            setSocialLinks(response.data);
        } catch (error) {
            console.error('Error fetching social links:', error);
        } finally {
            setLoading(false);
        }
    };

    const addSocialLink = async (data) => {
        try {
            const socialLinkData = {
                type: data.type,
                link: data.link,
                clinicId: clinicId,
            };
            const response = await settingsService.addUpdateSocialLinks(clinicId, socialLinkData);
            console.log("Social Resposne : ",response);
            console.log("Prev ValuesocialLink : ",socialLinks);
            setSocialLinks((prev) => [...prev, response.data]);
            setAddingNew(false);
            reset();
        } catch (error) {
            console.error('Error adding social link:', error);
        }
    };

    const updateSocialLink = async (socialLinkId, data) => {
        try {
            const updatedSocialLinkData = {
                id: socialLinkId,
                type: data.type,
                link: data.link,
                clinicId: clinicId,
            };
            const response = await settingsService.addUpdateSocialLinks(clinicId, updatedSocialLinkData);
            setSocialLinks((prev) =>
                prev.map((socialLink) =>
                    socialLink.id === socialLinkId
                        ? { ...socialLink, ...response.data, type: data.type, link: data.link }
                        : socialLink
                )
            );
            setEditingId(null);
        } catch (error) {
            console.error('Error updating social link:', error);
        }
    };

    const deleteSocialLink = async (socialLinkId) => {
        try {
            await settingsService.deleteSocialLink(clinicId, socialLinkId);
            setSocialLinks((prev) => prev.filter((socialLink) => socialLink.id !== socialLinkId));
            setDeleteConfirm(null);
        } catch (error) {
            console.error('Error deleting social link:', error);
        }
    };

    const handleEdit = (socialLink) => {
        setEditingId(socialLink.id);
        setValue('type', socialLink.type);
        setValue('link', socialLink.link);
    };

    const handleCancel = () => {
        setEditingId(null);
        setAddingNew(false);
        reset();
    };

    const handleAddNew = () => {
        setAddingNew(true);
        setEditingId(null);
        reset({ type: 'WEBSITE', link: '' });
    };

    const getSocialLinkThumbnail = (type) => {
        const socialType = socialLinkTypes.find(st => st.value === type);
        return socialType ? socialType.thumbnail : socialLinkTypes[0].thumbnail;
    };

    const getSocialLinkLabel = (type) => {
        const socialType = socialLinkTypes.find(st => st.value === type);
        return socialType ? socialType.label : type;
    };

    const selectedType = watch('type');
    const currentThumbnail = addingNew || editingId ?
        getSocialLinkThumbnail(selectedType) : null;

    useEffect(() => {
        fetchSocialLinks();
    }, []);

    const getPlatformIcon = (type) => {
        const icons = {
            WEBSITE: "bi-globe",
            GOOGLE_PAGE: "bi-google",
            FACEBOOK: "bi-facebook",
            INSTAGRAM: "bi-instagram",
            TWITTER: "bi-twitter",
            YELP: "bi-star-fill",
            OTHER: "bi-link-45deg"
        };
        return icons[type] || "bi-link-45deg";
    };

    const formatPlatformName = (type) => {
        return type.replace('_', ' ');
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-0">
                        <i className="bi bi-share me-2 text-primary"></i> Social Links
                    </h4>
                    <p className="text-muted mb-0">Manage your clinic's social media and website links</p>
                </div>
                {!addingNew && (
                    <button className="btn btn-outline-primary" onClick={handleAddNew}>
                        <i className="bi bi-plus-circle me-2"></i> Add New Link
                    </button>
                )}
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading social links...</p>
                </div>
            ) : socialLinks.length === 0 && !addingNew ? (
                <div className="text-center py-5">
                    <i className="bi bi-link-45deg display-1 text-muted"></i>
                    <h5 className="text-muted mt-3">No social links found</h5>
                    <p className="text-muted">Get started by adding your first social media or website link</p>
                    <button className="btn btn-primary mt-2" onClick={handleAddNew}>
                        <i className="bi bi-plus-circle me-2"></i> Add First Link
                    </button>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover align-middle table-striped">
                        <thead>
                        <tr>
                            <th scope="col" className="ps-4"><i className="bi bi-share me-2"></i> Platform</th>
                            <th scope="col">Link</th>
                            <th scope="col" className="text-end pe-4">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {/* Add New Row */}
                        {addingNew && (
                            <tr className="border-top bg-light position-relative">
                                <td className="ps-4">
                                    <div className="d-flex align-items-center">

                                        <div className="flex-grow-1">
                                            <select
                                                className={`form-select ${errors.type ? 'is-invalid' : ''}`}
                                                {...register('type', { required: 'Platform type is required' })}
                                            >
                                                {socialLinkTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.type && <div className="invalid-feedback">{errors.type.message}</div>}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <input
                                        type="url"
                                        className={`form-control ${errors.link ? 'is-invalid' : ''}`}
                                        placeholder="https://example.com"
                                        {...register('link', {
                                            required: 'Link is required',
                                            pattern: {
                                                value: /^https?:\/\/.+\..+/,
                                                message: 'Please enter a valid URL starting with http:// or https://'
                                            }
                                        })}
                                    />
                                    {errors.link && <div className="invalid-feedback">{errors.link.message}</div>}
                                </td>

                                <td className="text-end pe-4">
                                    <div className="btn-group">
                                        <button className="btn btn-success" onClick={handleSubmit(addSocialLink)}>
                                            <i className="bi bi-check"></i>
                                        </button>
                                        <button className="btn btn-outline-secondary" onClick={handleCancel}>
                                            <i className="bi bi-x"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {/* Existing Social Links */}
                        {socialLinks.map((socialLink) => (
                            <tr key={socialLink.id} className="border-top position-relative">
                                {/* Delete Confirmation Overlay */}
                                {deleteConfirm?.id === socialLink.id && (
                                    <td className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-90 d-flex justify-content-between align-items-center px-4" style={{ zIndex: 10 }}>
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-exclamation-triangle-fill text-danger me-2 fs-4"></i>
                                            <strong>Confirm delete "{getSocialLinkLabel(socialLink.type)}" link?</strong>
                                        </div>
                                        <div className="btn-group">
                                            <button className="btn btn-danger text-white" onClick={() => deleteSocialLink(socialLink.id)}>
                                                <i className="bi bi-trash me-1"></i> Confirm
                                            </button>
                                            <button className="btn btn-outline-secondary" onClick={() => setDeleteConfirm(null)}>
                                                Cancel
                                            </button>
                                        </div>
                                    </td>
                                )}

                                <td className="ps-4">
                                    <div className="d-flex align-items-center">

                                         <i className={`bi ${getPlatformIcon(socialLink.type)} me-2`}></i>
                                        <div className="flex-grow-1">
                                            {editingId === socialLink.id ? (
                                                <select
                                                    className={`form-select ${errors.type ? 'is-invalid' : ''}`}
                                                    defaultValue={socialLink.type}
                                                    {...register('type', { required: 'Platform type is required' })}
                                                >
                                                    {socialLinkTypes.map((type) => (
                                                        <option key={type.value} value={type.value}>
                                                            {type.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <>
                                                    <h6 className="mb-0 fw-semibold">{getSocialLinkLabel(socialLink.type)}</h6>

                                                </>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    {editingId === socialLink.id ? (
                                        <input
                                            type="url"
                                            className={`form-control ${errors.link ? 'is-invalid' : ''}`}
                                            defaultValue={socialLink.link}
                                            {...register('link', {
                                                required: 'Link is required',
                                                pattern: {
                                                    value: /^https?:\/\/.+\..+/,
                                                    message: 'Please enter a valid URL starting with http:// or https://'
                                                }
                                            })}
                                        />
                                    ) : (
                                        <a
                                            href={socialLink.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-decoration-none"
                                        >
                                            <span className="text-truncate d-inline-block" style={{ maxWidth: '200px' }}>
                                                {socialLink.link}
                                            </span>
                                            <i className="bi bi-box-arrow-up-right ms-1 text-muted small"></i>
                                        </a>
                                    )}
                                </td>
                                 <td className="text-end pe-4">
                                    <div className="btn-group">
                                        {editingId === socialLink.id ? (
                                            <>
                                                <button className="btn btn-success" onClick={handleSubmit((data) => updateSocialLink(socialLink.id, data))}>
                                                    <i className="bi bi-check"></i>
                                                </button>
                                                <button className="btn btn-outline-secondary" onClick={handleCancel}>
                                                    <i className="bi bi-x"></i>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button className="btn btn-outline-info" onClick={() => handleEdit(socialLink)}>
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button className="btn btn-outline-danger" onClick={() => setDeleteConfirm(socialLink)}>
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

export default SocialLinks;